import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { resolveUrl } from '../../utils/api';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function FilePreviewModal({ file, onClose }) {
  const abs = resolveUrl(file.url);
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.previewModal} onClick={e => e.stopPropagation()}>
        <div style={S.previewHdr}>
          <div>
            <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 12 }}>{file.name}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{file.path} · {formatSize(file.size)}</div>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.previewBody}>
          {file.file_type === 'image' && (
            <img src={abs} alt={file.name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 10, objectFit: 'contain' }}
              onError={e => { e.target.outerHTML = '<div style="color:#475569;padding:40px;text-align:center">Image not available</div>'; }} />
          )}
          {file.file_type === 'video' && (
            <video src={abs} controls style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 10 }} />
          )}
          {file.file_type === 'pdf' && (
            <iframe src={abs} style={{ width: '100%', height: 500, borderRadius: 10, border: 'none' }} title="PDF Preview" />
          )}
          {!['image', 'video', 'pdf'].includes(file.file_type) && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 46, marginBottom: 12 }}>📄</div>
              <div style={{ color: '#94a3b8' }}>{file.name}</div>
              <a href={abs} download target="_blank" rel="noreferrer" style={S.downloadBtn}>⬇ Download</a>
            </div>
          )}
        </div>
        <div style={S.previewFooter}>
          <a href={abs} download target="_blank" rel="noreferrer" style={S.downloadLink}>⬇ Download File</a>
        </div>
      </div>
    </div>
  );
}

const CATEGORIES = ['avatars', 'chat_files', 'random_chat_files', 'posts'];
const FILE_TYPES = ['image', 'video', 'pdf', 'audio', 'file'];

export default function AdminFiles() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [fileType, setFileType] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [previewFile, setPreviewFile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (fileType) params.append('file_type', fileType);
      const res = await api.get(`/admin/files/?${params}`);
      setFiles(res.data.files);
      setTotal(res.data.total);
      setTotalSize(res.data.total_size);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [search, category, fileType]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const deleteFile = async (file) => {
    try {
      await api.delete('/admin/files/delete/', { data: { path: file.path } });
      setFiles(prev => prev.filter(f => f.path !== file.path));
      setConfirmDelete(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  };

  const getFileIcon = (ft) => {
    if (ft === 'image') return '🖼️';
    if (ft === 'video') return '🎬';
    if (ft === 'pdf') return '📄';
    if (ft === 'audio') return '🎵';
    return '📎';
  };

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>📁 Files</h1>
          <p style={S.subtitle}>{total} files · {formatSize(totalSize)} total</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...S.viewBtn, ...(viewMode === 'grid' ? S.viewActive : {}) }} onClick={() => setViewMode('grid')}>⊞ Grid</button>
          <button style={{ ...S.viewBtn, ...(viewMode === 'list' ? S.viewActive : {}) }} onClick={() => setViewMode('list')}>☰ List</button>
        </div>
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <input
          style={S.searchInput}
          placeholder="Search files..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={S.select} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={S.select} value={fileType} onChange={e => setFileType(e.target.value)}>
          <option value="">All Types</option>
          {FILE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Stats row */}
      <div style={S.statsRow}>
        {FILE_TYPES.map(t => {
          const count = files.filter(f => f.file_type === t).length;
          if (!count) return null;
          return (
            <div key={t} style={S.statChip}>
              <span>{getFileIcon(t)}</span>
              <span style={{ fontWeight: 700, color: '#e2e8f0' }}>{count}</span>
              <span style={{ color: '#475569', fontSize: 10 }}>{t}s</span>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : files.length === 0 ? (
        <div style={S.center}><p style={{ color: '#475569' }}>No files found</p></div>
      ) : viewMode === 'grid' ? (
        <div style={S.grid}>
          {files.map(file => (
            <div key={file.path} style={S.fileCard}>
              {/* Thumbnail */}
              <div style={S.thumbWrap} onClick={() => setPreviewFile(file)}>
                {file.file_type === 'image' ? (
                  <img
                    src={resolveUrl(file.url)}
                    alt={file.name}
                    style={S.thumbImg}
                    loading="lazy"
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div style={{ ...S.thumbIcon, display: file.file_type === 'image' ? 'none' : 'flex' }}>
                  <span style={{ fontSize: 34 }}>{getFileIcon(file.file_type)}</span>
                </div>
                <div style={S.thumbOverlay}>
                  <span style={S.viewHint}>👁 View</span>
                </div>
              </div>
              <div style={S.fileInfo}>
                <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                  {file.name}
                </div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{file.category}</span>
                  <span>{formatSize(file.size)}</span>
                </div>
              </div>
              <button style={S.fileDeleteBtn} onClick={() => setConfirmDelete(file)} title="Delete file">🗑️</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Type', 'Name', 'Category', 'Size', 'Actions'].map(h => (
                  <th key={h} style={S.thCell}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.map(file => (
                <tr key={file.path} style={S.tr}>
                  <td style={S.td}><span style={{ fontSize: 16 }}>{getFileIcon(file.file_type)}</span></td>
                  <td style={{ ...S.td, maxWidth: 240 }}>
                    <button style={S.nameLinkBtn} onClick={() => setPreviewFile(file)}>
                      {file.name}
                    </button>
                  </td>
                  <td style={{ ...S.td, color: '#64748b', fontSize: 10 }}>{file.category}</td>
                  <td style={{ ...S.td, color: '#64748b', fontSize: 10 }}>{formatSize(file.size)}</td>
                  <td style={S.td}>
                    <button style={S.dangerActionBtn} onClick={() => setConfirmDelete(file)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div style={S.modalOverlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#f43f5e' }}>⚠️ Delete File?</span>
              <button style={S.closeBtn} onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: 12 }}>
                Permanently delete <strong style={{ color: '#e2e8f0' }}>{confirmDelete.name}</strong> ({formatSize(confirmDelete.size)})?
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button style={S.dangerBtn} onClick={() => deleteFile(confirmDelete)}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', padding: 24, background: '#07070d', display: 'flex', flexDirection: 'column', gap: 14, animation: 'fadeIn 0.3s ease' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: '#e2e8f0' },
  subtitle: { margin: '4px 0 0', color: '#475569', fontSize: 11 },
  viewBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  viewActive: { background: 'rgba(99,102,241,0.15)', color: '#818cf8', borderColor: 'rgba(99,102,241,0.25)' },
  filters: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  searchInput: { padding: '9px 15px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', flex: 1, minWidth: 160, boxSizing: 'border-box' },
  select: { padding: '9px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(20,20,30,0.95)', color: '#94a3b8', fontSize: 11, outline: 'none', cursor: 'pointer', minWidth: 120 },
  statsRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  statChip: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', fontSize: 11 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 },
  spinner: { width: 28, height: 28, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
  fileCard: { position: 'relative', background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' },
  thumbWrap: { position: 'relative', height: 130, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0e0e1a', overflow: 'hidden' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbIcon: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  thumbOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'all 0.2s', ':hover': { background: 'rgba(0,0,0,0.5)', opacity: 1 } },
  viewHint: { color: '#fff', fontSize: 11, fontWeight: 600, background: 'rgba(99,102,241,0.7)', padding: '4px 12px', borderRadius: 20 },
  fileInfo: { padding: '8px 10px' },
  fileDeleteBtn: { position: 'absolute', top: 8, right: 8, background: 'rgba(244,63,94,0.2)', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 10, backdropFilter: 'blur(4px)' },
  tableWrap: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  thCell: { padding: '12px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '10px 16px', verticalAlign: 'middle' },
  nameLinkBtn: { background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: 11, textAlign: 'left', padding: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 220, display: 'block' },
  dangerActionBtn: { background: 'rgba(244,63,94,0.1)', border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, width: '100%' },
  previewModal: { background: '#111118', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,0.7)' },
  previewHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  previewBody: { flex: 1, overflow: 'auto', padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f', borderRadius: '0 0 0 0' },
  previewFooter: { padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' },
  modalHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 },
  downloadBtn: { display: 'inline-block', marginTop: 16, padding: '8px 20px', borderRadius: 10, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: 12, textDecoration: 'none' },
  downloadLink: { color: '#818cf8', fontSize: 11, fontWeight: 600, textDecoration: 'none' },
  dangerBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(244,63,94,0.25)', background: 'rgba(244,63,94,0.12)', color: '#f43f5e', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  cancelBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};
